// ─── <ImageUploader/> ────────────────────────────────────────────────────────
// Drop-in replacement for the old "<input value={imageUrl}>" pattern that was
// repeated across AdminProductsTab (product form, kit form) and AdminMaterialsTab.
// Admin can now upload a file OR paste a URL — either way, onChange fires with
// the final URL string.
//
// Uploads go through uploadProductImage, which pads to 1000×1000 square JPEG
// before sending to the product-images Supabase Storage bucket. Every thumbnail
// in the app uses a square container + object-fit:cover, so the padded upload
// displays in full with no cropping at 140×140, 80×80, 40×40, etc.

import React, { useRef, useState } from 'react';
import { uploadProductImage } from './imageUpload';

export default function ImageUploader({ value = "", onChange, folder = "products", label = "Product image" }) {
  const inputRef = useRef(null);
  const [status, setStatus] = useState({ kind: "idle", msg: "", pct: 0 });
  const [dragging, setDragging] = useState(false);

  const handleFile = async (file) => {
    setStatus({ kind: "uploading", msg: "Preparing…", pct: 10 });
    try {
      const url = await uploadProductImage(file, {
        folder,
        onProgress: (pct) => setStatus(s => ({ ...s, pct, msg: pct < 50 ? "Resizing…" : pct < 95 ? "Uploading…" : "Finishing…" }))
      });
      if(onChange) onChange(url);
      setStatus({ kind: "success", msg: "Uploaded!", pct: 100 });
      // Clear success message after a moment so the UI doesn't get crowded.
      setTimeout(() => setStatus({ kind: "idle", msg: "", pct: 0 }), 2000);
    } catch(e) {
      console.warn("Image upload failed:", e?.message || e);
      setStatus({ kind: "error", msg: e?.message || "Upload failed. Try again.", pct: 0 });
    }
  };

  const onPicked = (e) => {
    const f = e.target.files?.[0];
    if(f) handleFile(f);
    // Reset the input so picking the same file twice in a row re-fires.
    if(inputRef.current) inputRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if(f) handleFile(f);
  };

  const pickStyle = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 500,
    background: "#F1F5F9", color: "#334155", border: "1px solid #CBD5E1",
    cursor: "pointer"
  };
  const clearStyle = { ...pickStyle, color: "#991B1B", border: "1px solid #FCA5A5", background: "#FEF2F2" };
  const inputStyle = {
    width: "100%", padding: "8px 10px",
    border: `1px solid ${dragging ? "#60A5FA" : "#E2E8F0"}`,
    background: dragging ? "#EFF6FF" : "white",
    borderRadius: 8, fontSize: 12, outline: "none", boxSizing: "border-box",
    transition: "border-color 0.15s, background 0.15s"
  };

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <label style={{ fontSize: 11, fontWeight: 500, color: "#64748B", marginBottom: 4, display: "block" }}>{label}</label>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        {value ? (
          <img
            src={value}
            alt=""
            style={{ width: 72, height: 72, borderRadius: 8, objectFit: "cover", border: "1px solid #E5E7EB", flexShrink: 0, background: "#F8FAFC" }}
            onError={e => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div style={{ width: 72, height: 72, borderRadius: 8, background: "#F1F5F9", border: "1px dashed #CBD5E1", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#94A3B8", textAlign: "center", flexShrink: 0, padding: 4, lineHeight: 1.3 }}>
            {dragging ? "Drop!" : "No image"}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            <button type="button" style={pickStyle} onClick={() => inputRef.current?.click()} disabled={status.kind === "uploading"}>
              {status.kind === "uploading" ? `${status.pct}%` : value ? "Replace image" : "Upload image"}
            </button>
            {value && status.kind !== "uploading" && (
              <button type="button" style={clearStyle} onClick={() => onChange && onChange("")}>Clear</button>
            )}
            <input ref={inputRef} type="file" accept="image/*" onChange={onPicked} style={{ display: "none" }}/>
          </div>
          <input
            style={inputStyle}
            value={value || ""}
            onChange={e => onChange && onChange(e.target.value)}
            placeholder="…or paste an image URL (https://…)"
            disabled={status.kind === "uploading"}
          />
          {status.kind === "uploading" && (
            <div style={{ marginTop: 6, height: 4, background: "#E5E7EB", borderRadius: 2, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${status.pct}%`, background: "#3B82F6", transition: "width 0.2s" }}/>
            </div>
          )}
          {status.msg && (
            <div style={{ marginTop: 6, fontSize: 11, color: status.kind === "error" ? "#B91C1C" : status.kind === "success" ? "#047857" : "#64748B" }}>
              {status.msg}
            </div>
          )}
          <div style={{ marginTop: 4, fontSize: 10, color: "#94A3B8", lineHeight: 1.5 }}>
            JPG or PNG. We auto-resize to a 1000×1000 square so it fits every product thumbnail cleanly.
          </div>
        </div>
      </div>
    </div>
  );
}
